import { PlaybackError } from './errors';
import { resolveCredential, type CredentialSource } from './types';

/**
 * Join a base URL and an absolute path into a validated absolute http(s) URL.
 * Rejects empty, protocol-relative (`//evil`), and non-http(s) bases up front so
 * an unvalidated baseUrl can never become the (credentialed) request origin.
 */
export function joinUrl(base: string, path: string): string {
  const trimmed = base.replace(/\/+$/, '');
  let url: URL;
  try {
    url = new URL(trimmed + path);
  } catch {
    throw new PlaybackError('INTERNAL', 0, `invalid baseUrl: ${JSON.stringify(base)}`);
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new PlaybackError('INTERNAL', 0, `baseUrl must be http(s), got: ${JSON.stringify(base)}`);
  }
  return url.toString();
}

/**
 * Parse an HTTP `Retry-After` header (delta-seconds or HTTP-date) into ms.
 * Returns undefined when absent/unparseable.
 */
export function parseRetryAfterMs(value: string | null): number | undefined {
  if (!value) return undefined;
  const secs = Number(value);
  if (Number.isFinite(secs)) return Math.max(0, Math.round(secs * 1000));
  const when = Date.parse(value);
  if (!Number.isNaN(when)) return Math.max(0, when - Date.now());
  return undefined;
}

export interface FetchOptions {
  credential: CredentialSource;
  signal?: AbortSignal;
  method?: string;
  headers?: Headers;
  body?: BodyInit;
  /**
   * Extra headers for this request — and strictly extra: an entry whose
   * name the SDK already set (e.g. `If-None-Match` on conditional catalog
   * and status polls) is ignored rather than allowed to clobber it, and
   * `x-api-key` is applied after this map so the credential can never be
   * replaced either.
   */
  apiHeaders?: Record<string, string>;
}

/**
 * fetch() with the `x-api-key` header attached from the (possibly async)
 * credential source. Network failures and aborts are normalized to
 * PlaybackError so callers handle one error type. The credential is resolved
 * per call so a getCredential() hook can refresh transparently.
 */
export async function fetchWithCredential(url: string, opts: FetchOptions): Promise<Response> {
  const cred = await resolveCredential(opts.credential);
  const headers = opts.headers ?? new Headers();
  if (opts.apiHeaders) {
    for (const [name, value] of Object.entries(opts.apiHeaders)) {
      if (!headers.has(name)) headers.set(name, value);
    }
  }
  headers.set('x-api-key', cred.apiKey);
  try {
    return await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body,
      signal: opts.signal,
      cache: 'no-store',
      credentials: 'omit',
      mode: 'cors',
    });
  } catch (err) {
    if (opts.signal?.aborted) {
      throw new PlaybackError('ABORTED', 0, 'request aborted', { cause: err });
    }
    throw new PlaybackError('NETWORK', 0, `network request failed: ${describe(err)}`, {
      cause: err,
    });
  }
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}
